/* =========================================
   LAZY SWIPER MANAGER
========================================= */
(() => {
  "use strict";

  const SWIPER_SELECTOR = ".swiper-new";
  const instances = new WeakMap();

  let intersectionObserver = null;

  /* -----------------------------------------
     HELPERS
  ----------------------------------------- */
  const parseNumber = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getSwiperContainers = (root = document) => {
    const containers = [];

    if (
      root instanceof Element &&
      root.matches(SWIPER_SELECTOR)
    ) {
      containers.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      containers.push(...root.querySelectorAll(SWIPER_SELECTOR));
    }

    return containers;
  };

  const supportsDesktopMousewheel = () => {
    return window.matchMedia(
      "(hover: hover) and (pointer: fine)"
    ).matches;
  };

  /* -----------------------------------------
     INITIALIZE ONE SWIPER
  ----------------------------------------- */
  const initSingleSwiper = (container) => {
    if (!container || !container.isConnected) return;

    if (instances.has(container)) return;

    if (
      container.dataset.swiperState === "initializing" ||
      container.dataset.swiperState === "initialized"
    ) {
      return;
    }

    if (typeof window.Swiper !== "function") {
      console.warn(
        "Swiper library is unavailable:",
        container
      );
      return;
    }

    container.dataset.swiperState = "initializing";

    const slidesXs = parseNumber(
      container.dataset.slidesXs,
      1
    );

    const slidesSm = parseNumber(
      container.dataset.slidesSm,
      slidesXs
    );

    const slidesMd = parseNumber(
      container.dataset.slidesMd,
      slidesSm
    );

    const slidesLg = parseNumber(
      container.dataset.slidesLg,
      slidesMd
    );

    const slidesXl = parseNumber(
      container.dataset.slidesXl,
      slidesLg
    );

    const slidesXxl = parseNumber(
      container.dataset.slidesXxl,
      slidesXl
    );

    const spaceBetween = parseNumber(
      container.dataset.spaceBetween,
      10
    );

    const slides = container.querySelectorAll(
      ".swiper-wrapper > .swiper-slide"
    );

    const slideCount = slides.length;

    const maximumSlidesPerView = Math.max(
      slidesXs,
      slidesSm,
      slidesMd,
      slidesLg,
      slidesXl,
      slidesXxl
    );

    /*
      Looping creates duplicate slides and adds layout work.

      Enable it only when the carousel has more slides than
      the largest configured slides-per-view value.
    */
    const shouldLoop =
      container.dataset.loop !== "false" &&
      slideCount > Math.ceil(maximumSlidesPerView);

    try {
      const swiper = new window.Swiper(container, {
        direction: "horizontal",

        slidesPerView: slidesXs,
        spaceBetween,

        watchOverflow: true,

        /*
          Prevent Swiper from loading all images eagerly.
          Keep this only if your current Swiper version
          supports this lazy-loading configuration.
        */
        preloadImages: false,

        lazy: {
          enabled: true,
          loadPrevNext: true,
          loadOnTransitionStart: false,
        },

        freeMode: {
          enabled: true,
          momentum: true,
          momentumBounce: true,
          sticky: false,
        },

        /*
          Mousewheel is useful on desktop, but unnecessary
          on touch devices and mobile phones.
        */
        mousewheel: supportsDesktopMousewheel()
          ? {
              forceToAxis: true,
              sensitivity: 1,
              releaseOnEdges: true,
            }
          : false,

        loop: shouldLoop,

        breakpoints: {
          0: {
            slidesPerView: slidesXs,
          },

          480: {
            slidesPerView: slidesSm,
          },

          768: {
            slidesPerView: slidesMd,
          },

          1024: {
            slidesPerView: slidesLg,
          },

          1440: {
            slidesPerView: slidesXl,
          },

          2560: {
            slidesPerView: slidesXxl,
          },
        },

        on: {
          init() {
            container.dataset.swiperState = "initialized";
          },

          destroy() {
            delete container.dataset.swiperState;
          },
        },
      });

      instances.set(container, swiper);
      container.dataset.swiperState = "initialized";
    } catch (error) {
      delete container.dataset.swiperState;

      console.error(
        "Swiper initialization failed:",
        container,
        error
      );
    }
  };

  /* -----------------------------------------
     SCHEDULE INITIALIZATION

     This avoids initializing several sliders
     in the same rendering frame.
  ----------------------------------------- */
  const scheduleSwiperInitialization = (container) => {
    if (!container || instances.has(container)) return;

    const run = () => {
      initSingleSwiper(container);
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(run, {
        timeout: 500,
      });
    } else {
      window.requestAnimationFrame(run);
    }
  };

  /* -----------------------------------------
     SHARED INTERSECTION OBSERVER
  ----------------------------------------- */
  const getIntersectionObserver = () => {
    if (intersectionObserver) {
      return intersectionObserver;
    }

    if (!("IntersectionObserver" in window)) {
      return null;
    }

    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          intersectionObserver.unobserve(entry.target);
          scheduleSwiperInitialization(entry.target);
        });
      },
      {
        /*
          Begin initialization before the slider becomes
          visible, without initializing the whole page.
        */
        rootMargin: "400px 0px",
        threshold: 0,
      }
    );

    return intersectionObserver;
  };

  /* -----------------------------------------
     DISCOVER NEW SWIPERS
  ----------------------------------------- */
  const observeSwipers = (root = document) => {
    const containers = getSwiperContainers(root);

    if (!containers.length) return;

    const observer = getIntersectionObserver();

    containers.forEach((container) => {
      if (instances.has(container)) return;

      if (
        container.dataset.swiperState === "initializing" ||
        container.dataset.swiperState === "initialized"
      ) {
        return;
      }

      if (observer) {
        observer.observe(container);
      } else {
        scheduleSwiperInitialization(container);
      }
    });
  };

  /* -----------------------------------------
     DESTROY SWIPERS IN REMOVED SECTION
  ----------------------------------------- */
  const destroySwipers = (root) => {
    const containers = getSwiperContainers(root);

    containers.forEach((container) => {
      intersectionObserver?.unobserve(container);

      const swiper =
        instances.get(container) ||
        container.swiper;

      if (
        swiper &&
        typeof swiper.destroy === "function"
      ) {
        swiper.destroy(true, true);
      }

      instances.delete(container);
      delete container.dataset.swiperState;
    });
  };

  /* -----------------------------------------
     INITIAL PAGE LOAD
  ----------------------------------------- */
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => observeSwipers(document),
      { once: true }
    );
  } else {
    observeSwipers(document);
  }

  /* -----------------------------------------
     SHOPIFY THEME EDITOR
  ----------------------------------------- */
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      observeSwipers(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      destroySwipers(event.target);
    }
  );
})();